<script lang="ts" setup>
import {
  Cell,
  CellGroup,
  Popup,
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { computed, ref, watch } from "vue";
// import HappyFaceIcon from "@/components/icon/HappyFaceIcon.vue";
import { useMate } from "@/stores/mate";
import EmojiVotePopup from "@/components/mobile/popup/EmojiVotePopup.vue";
import { useMoment } from "@/stores/moment";
import { useReply } from "@/stores/reply";
import { useRoute, useRouter } from "vue-router";
import { ApiDeleteCollectionItem } from "@/apis/collection";
import { useI18n } from "vue-i18n";
import {
  ApiPutReplyBlockOne,
  ApiPostUserBlockOne,
  ApiPostReport,
} from "@/apis/feedback";
import { CollectionItem } from "@/types/collection";
import { Reply, Moment } from "@/types/entity";
import { useGlobalModal } from "@/stores/globalModal";
import { ReplyEntityType } from "@/enums";
import { sessionStorageSet } from "@/tools/sessionStorage";
import { removeHtmlTag } from "@/tools/text";

const router = useRouter();
const props = defineProps<{
  show: boolean;
  moment?: Moment;
  reply?: Reply;
  replyMoment?: Moment;
  collectionItem?: CollectionItem;
  showEmoji?: number;
  hideEmoji?: boolean;
}>();
const emit = defineEmits<{
  (e: "update:show", isShow: boolean): void;
  (e: "afterDelete", uuid: string): void;
}>();

const momentStore = useMoment();
const mateStore = useMate();
const emojiShow = ref<boolean>(false);
const route = useRoute();
const { t } = useI18n();
const modalStore = useGlobalModal();
const showMomentDelete = computed(
  () =>
    props.moment &&
    mateStore.isMyMateById(props.moment.mate?.id) &&
    (!props.moment.childId || !props.moment.parentId)
);

const isMyMoment = computed(
  () => props.replyMoment && mateStore.isMyMateById(props.replyMoment.mate?.id)
);

const isMyReply = computed(
  () => props.reply && mateStore.isMyMateById(props.reply.mate?.id)
);
const toVoteReply = ref<Reply | null>(null);

const showCollectionItem = computed(() => !!props.collectionItem);

// LifeCycle
watch(
  () => props.show,
  (show) => {
    if (show) {
      emojiShow.value = false;
    }
  }
);

const isTreehole = computed(() => {
  return (
    (props.moment && props.moment?.type === 2) ||
    (props.replyMoment && props.replyMoment.type === 2)
  );
});

watch(
  () => props.showEmoji,
  () => {
    triggerVote();
  }
);

// EventHandler
const triggerVote = () => {
  toVoteReply.value = props.reply ?? props.moment!.reply;
  emit("update:show", false);
  emojiShow.value = true;
};

const onMomentEditClick = async () => {
  if (!props.moment) return emit("update:show", false);
  const { uuid, mate } = props.moment;
  await mateStore.changeCurrentMate(mate!.id!);
  emit("update:show", false);
  router.push({
    name: "createThread",
    query: { uuid },
  });
};

const onMomentDeleteClick = async () => {
  if (!props.moment) {
    emit("update:show", false);
    return;
  }
  try {
    await showConfirmDialog({
      title: isTreehole.value
        ? t("ConfirmModal.confirmDeleteThisSecret")
        : t("ConfirmModal.confirmDeleteThisMoment"),
      message: isTreehole.value
        ? t("ConfirmModal.deleteSecretCannotRecovered")
        : t("ConfirmModal.deleteMomentCannotRecovered"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    return;
  }
  const loadingToast = showLoadingToast({
    message: t("Message.deleting") + "...",
    duration: 0,
  });
  try {
    await momentStore.deleteMoment(props.moment.uuid);
    emit("afterDelete", props.moment.uuid);
  } finally {
    emit("update:show", false);
    loadingToast.close();
  }
};

const replyStore = useReply();
const onReplyDeleteClick = async () => {
  if (!props.reply) {
    emit("update:show", false);
    return;
  }
  try {
    await showConfirmDialog({
      title: t("ConfirmModal.confirmDeleteReply"),
      message: t("ConfirmModal.deleteReplyCannotRecovered"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    return;
  }
  const loadingToast = showLoadingToast({
    message: t("Message.deleting") + "...",
    duration: 0,
  });
  try {
    await replyStore.deleteReply(props.reply.id!);
  } finally {
    emit("update:show", false);
    loadingToast.close();
  }
};

const onCollectionItemClick = async () => {
  if (!props.collectionItem) {
    emit("update:show", false);
    return;
  }
  try {
    await showConfirmDialog({
      title: t("ConfirmModal.confirmDeleteCollectionItem"),
      confirmButtonText: t("Buttons.confirm"),
      cancelButtonText: t("Buttons.cancel"),
    });
  } catch {
    return;
  }
  const loadingToast = showLoadingToast({
    message: t("Message.deleting") + "...",
    duration: 0,
  });
  try {
    await ApiDeleteCollectionItem(props.collectionItem.id!);
    emit("afterDelete", "");
  } finally {
    emit("update:show", false);
    loadingToast.close();
  }
};

// 编辑动态
// const showEditMoment = ref(false);

const onReplyEditClick = async () => {
  emit("update:show", false);
  if (props.reply) {
    modalStore.openReplyEditModal({
      moment: props.replyMoment,
      reply: props.reply,
      type: ReplyEntityType.Moment,
      entity: props.replyMoment,
      mate: props.reply.mate,
    });
  }
};

// 删除举报拉黑
const clickDelete = () => {
  showConfirmDialog({
    message: t(`Message.deleteReplyConfirm`),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      const loading = showLoadingToast(t(`Message.loading`));
      try {
        await ApiPutReplyBlockOne(props.reply!.id!);
        replyStore.getReply(props.reply!.id!)!.content = t(
          `Message.blockReplyContent`
        );
        loading.close();
        showSuccessToast(t(`Message.opSuccess`));
        emit("update:show", false);
      } catch (e) {
        loading.close();
      }
    })
    .catch();
};

const showReportInput = ref(false);

const clickReportConfirm = async (reportContent: string) => {
  if (!reportContent) return showFailToast(t(`Message.reportTip`));

  const loading = showLoadingToast(t(`Message.loading`));
  try {
    let reportableId = 0;
    let reportableType = "";
    if (props.reply) {
      reportableId = props.reply.id!;
      reportableType = "App\\Models\\Reply";
    } else if (props.moment) {
      reportableId = props.moment.id!;
      reportableType = "App\\Models\\Moment";
    }
    await ApiPostReport({
      reason: reportContent,
      reportableType,
      reportableId,
    });
    loading.close();
    showSuccessToast(t(`Message.opSuccess`));
    emit("update:show", false);
  } catch (e) {
    loading.close();
  }

  showReportInput.value = false;
};

const clickBlockUser = () => {
  showConfirmDialog({
    message: t(`Message.blockUserConfirm`),
    confirmButtonText: t("Buttons.confirm"),
    cancelButtonText: t("Buttons.cancel"),
  })
    .then(async () => {
      const loading = showLoadingToast(t(`Message.loading`));
      try {
        if (props.reply) {
          await ApiPostUserBlockOne(props.reply!.mate.userId!);
        } else if (props.moment) {
          await ApiPostUserBlockOne(props.moment!.mate.userId!);
        }
        loading.close();
        showSuccessToast(t(`Message.opSuccess`));
        emit("update:show", false);
      } catch (e) {
        loading.close();
      }
    })
    .catch();
};

const clickMateWish = () => {
  // const {
  //   text,
  //   mate: { id },
  // } = props.moment;
  sessionStorageSet("mate-wish-edit", {
    content: removeHtmlTag(props.moment?.text ?? ""),
    characterIdzz: [props.moment?.mate?.id],
  });
  emit("update:show", false);
  setTimeout(() => {
    router.push({ name: `mateWishEdit` });
  }, 50);
};
</script>
<template>
  <popup
    :class="['py-4 flex flex-col gap-4']"
    :show="show"
    position="bottom"
    round
    teleport="body"
    @update:show="emit('update:show', $event)"
  >
    <cell-group inset>
      <cell
        v-if="!hideEmoji && reply && !moment && !isTreehole"
        is-link
        @click="triggerVote"
      >
        <template #title>
          <div class="flex items-center gap-2">
            <happy-face-icon />
            <div>{{ $t("Moment.expression") }}</div>
          </div>
        </template>
      </cell>
      <cell
        v-if="
          showMomentDelete &&
          !(
            (moment?.childId || moment?.parentId) &&
            route.name !== 'momentChain'
          ) &&
          !isTreehole
        "
        :title="isTreehole ? $t('Cell.editSecret') : $t('Cell.editMoment')"
        clickable
        @click="onMomentEditClick"
      />
      <cell
        v-if="showMomentDelete"
        :title="isTreehole ? $t('Cell.deleteSecret') : $t('Cell.deleteMoment')"
        class="text-error"
        clickable
        @click="onMomentDeleteClick"
      />

      <cell
        v-if="
          showMomentDelete &&
          !(
            (moment?.childId || moment?.parentId) &&
            route.name !== 'momentChain'
          ) &&
          !isTreehole
        "
        :title="$t('mateWish.name')"
        clickable
        @click="clickMateWish"
      />
      <cell
        v-if="isMyReply && !reply?.dtBlock && !isTreehole"
        :title="$t('Cell.editReply')"
        clickable
        @click="onReplyEditClick"
      />
      <cell
        v-if="isMyReply && !reply?.dtBlock"
        :title="$t('Cell.deleteReply')"
        class="text-error"
        clickable
        @click="onReplyDeleteClick"
      />
      <cell
        v-if="showCollectionItem"
        :title="$t('Cell.deleteCollectionItem')"
        class="text-error"
        clickable
        @click="onCollectionItemClick"
      />
    </cell-group>
    <cell-group inset>
      <cell
        v-if="isMyMoment === true && isMyReply === false && !reply?.dtBlock"
        :title="$t('Cell.delete')"
        class="text-error"
        clickable
        @click="clickDelete"
      />

      <cell
        v-if="
          !mateStore.isMyMateById(moment?.mate?.id) &&
          !mateStore.isMyMateById(reply?.mate?.id) &&
          !isTreehole
        "
        :title="$t('Cell.report')"
        class="text-error"
        clickable
        @click="showReportInput = true"
      />

      <cell
        v-if="!showMomentDelete && !isMyReply && !isTreehole"
        :title="$t('Cell.blockUser')"
        class="text-error"
        clickable
        @click="clickBlockUser"
      />

      <cell
        :title="$t('Cell.cancel')"
        class="text-base-content/50"
        clickable
        @click="emit('update:show', false)"
      />
    </cell-group>
  </popup>

  <BaseInputModal
    v-model:show="showReportInput"
    :title="t(`Message.reportTip`)"
    @confirm="clickReportConfirm"
  />

  <!-- <VanDialog
    v-if="showEditMoment"
    v-model:show="showEditMoment"
    :showConfirmButton="false"
    :title="$t(`Reply.edit`)"
    class="w-[80%] max-w-md"
    closeOnClickOverlay
  >
    <div class="mt-3" />
    <ReplyInput
      :key="reply?.id"
      :moment="moment"
      :reply-target="reply"
      :update-reply="reply"
      @reply-updated="showEditMoment = false"
      :entity="moment!"
      :entity-type="ReplyEntityType.Moment"
      :mate="reply?.mate"
    />
  </VanDialog> -->
  <emoji-vote-popup v-model:show="emojiShow" :to-vote-reply="toVoteReply" />
</template>
